import { useState } from "react";
import { BAND_UI } from "@/lib/bands";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Home, Pencil, Star, GraduationCap } from "lucide-react";

const TYPE_ICON = { chore: Home, reading: BookOpen, goal: Star, homework: GraduationCap };

export const FrogPicker = ({ band, bandData, onPick }) => {
  const ui = BAND_UI[band];
  const [tab, setTab] = useState("homework");
  const [custom, setCustom] = useState("");
  const [guess, setGuess] = useState("");
  const needsGuess = band === "68" || band === "912";
  const timeGuess = needsGuess && guess ? parseInt(guess, 10) : null;

  const submit = (title, type) => onPick(title, type, timeGuess);

  return (
    <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-lg space-y-6">
      <div>
        <p className="font-display text-xl sm:text-2xl font-bold text-slate-800">{ui.pickPrompt}</p>
        <p className="text-sm text-slate-500 mt-1">Pick ONE frog. Just one.</p>
      </div>

      <div className="flex gap-2">
        <button
          data-testid="tab-homework"
          onClick={() => setTab("homework")}
          className={`px-5 py-3 rounded-2xl font-display font-semibold text-sm transition-colors ${tab === "homework" ? "bg-green-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          School frog
        </button>
        <button
          data-testid="tab-substitute"
          onClick={() => setTab("substitute")}
          className={`px-5 py-3 rounded-2xl font-display font-semibold text-sm transition-colors ${tab === "substitute" ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          No homework? Substitute frog
        </button>
      </div>

      {needsGuess && (
        <div className="flex items-center gap-3 bg-yellow-50 rounded-2xl p-4 border border-yellow-100">
          <p className="text-sm font-semibold text-slate-700">How many minutes will it take?</p>
          <Input
            data-testid="time-guess-input"
            type="number"
            min="1"
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            placeholder="30"
            className="w-24 h-11 rounded-xl text-center font-bold"
          />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {(tab === "homework"
          ? bandData.frog_ideas.map((t) => ({ title: t, type: "homework" }))
          : bandData.substitutes
        ).map((item) => {
          const Icon = TYPE_ICON[item.type] || Star;
          return (
            <button
              key={item.title}
              data-testid={`frog-option-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => submit(item.title, item.type)}
              className="btn-tactile bg-[#F0FDF4] hover:bg-green-100 rounded-2xl p-5 text-left border border-green-100 flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-sm">
                <Icon className="w-5 h-5 text-green-600" />
              </div>
              <span className="font-display font-semibold text-slate-800">{item.title}</span>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3 items-center pt-2 border-t border-slate-100">
        <Pencil className="w-5 h-5 text-slate-400 shrink-0" />
        <Input
          data-testid="custom-frog-input"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && custom.trim() && submit(custom.trim(), tab === "homework" ? "homework" : "goal")}
          placeholder="Or type your own frog..."
          className="h-13 rounded-xl h-12"
        />
        <Button
          data-testid="custom-frog-submit"
          disabled={!custom.trim()}
          onClick={() => submit(custom.trim(), tab === "homework" ? "homework" : "goal")}
          className="btn-tactile h-12 rounded-xl bg-green-500 hover:bg-green-600 font-display font-semibold px-6"
        >
          Pick it
        </Button>
      </div>
    </div>
  );
};
